import { PlusOutlined } from '@ant-design/icons';
import { Button, Card, Empty, Flex, Spin } from 'antd';
import { useContext, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { NavigateFunction, useNavigate, useParams } from 'react-router-dom';
import { FormValidationContext, useOnNext } from '@hooks/useBtlFormValidation';
import { useBtlApplicationInProgressById } from '@queries/btlApplications/useApplicationInProgressById/useBtlApplicationInProgressById';
import { getPageURL, PageURL } from '../../../router/pageURL';
import { TypedFormatMessageFn } from '../../../types/intl';
import { BtlApplicationFormPageEnum, BtlFormSectionParam } from '../types';
import { useBtlFormMappedRoutes } from '../useBtlFormMappedRoutes';
import { CompanyCard } from './Company/CompanyCard';
import { IndividualCard } from './Individual/IndividualCard';

export const updateUrl = (
  pageUrl: PageURL,
  section: BtlApplicationFormPageEnum,
  navigate: NavigateFunction,
  id: {
    applicationId: string;
    individualId?: string;
    companyId?: string;
    householdId?: string;
  },
) => {
  const searchParams = new URLSearchParams({ section });
  if (id.companyId) searchParams.set('companyId', id.companyId);
  if (id.individualId) searchParams.set('individualId', id.individualId);
  if (id.householdId) searchParams.set('householdId', id.householdId);
  const editBtlApplicationUrl = `${getPageURL(pageUrl, { id: id.applicationId })}?${searchParams.toString()}`;
  navigate(editBtlApplicationUrl);
};

export const Applicants = () => {
  const { id = '' } = useParams();
  const { formatMessage }: { formatMessage: TypedFormatMessageFn } = useIntl();
  const navigate = useNavigate();
  const { data, isLoading, refetch } = useBtlApplicationInProgressById(id);
  const { nextSection, isFormEditable } = useBtlFormMappedRoutes(data);
  const formValidationContext = useContext(FormValidationContext);
  if (!formValidationContext) throw new Error('Applicants must be used within a FormValidationProvider');
  const { setIsValid } = formValidationContext;
  const MAX_INDIVIDUALS = 4;

  const individualOrder = data?.borrower.individualOrder || [];

  useEffect(() => {
    const isFormValid = data?.borrower.companies.length || data?.borrower.individuals.length;
    setIsValid(!!isFormValid);
  }, [data?.borrower]);

  useOnNext(async () => {
    if (nextSection) {
      updateSectionInUrl(nextSection);
    }
  });

  const updateSectionInUrl = (section: BtlFormSectionParam) => {
    navigate({
      search: `?section=${section}`,
    });
  };

  if (isLoading || isFormEditable === undefined) return <Spin size={'large'}></Spin>;

  const individualsToSort = data?.borrower.individuals || [];

  // only sort the individuals if the order is present on the borrower and matches the number of individuals
  if (individualOrder && individualOrder.length === individualsToSort.length) {
    individualsToSort.sort((a, b) => individualOrder.indexOf(a.id) - individualOrder.indexOf(b.id));
  }

  const individualCards = individualsToSort.map((individual, index) => {
    if (!data) return null;
    return (
      <IndividualCard
        key={index}
        individual={individual}
        applicationId={id}
        borrowerId={data.borrower.id}
        showRemoveAction={data?.borrower.individuals.length > 1}
        refetch={refetch}
        isEditable={!!isFormEditable}
      />
    );
  });

  const addIndividualCard = isFormEditable && (data?.borrower?.individuals?.length ?? 0) < MAX_INDIVIDUALS && (
    <Card
      actions={[
        <Button
          className={'w-full'}
          key='add'
          type={'link'}
          icon={<PlusOutlined />}
          onClick={() =>
            updateUrl(PageURL.BTL_APPLICATION_EDIT, BtlApplicationFormPageEnum.APPLICANTS_INDIVIDUAL, navigate, {
              applicationId: id,
            })
          }
        >
          {formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.addIndividual.label' })}
        </Button>,
      ]}
    >
      <Empty
        description={formatMessage({
          id:
            data?.borrower.individuals?.length === 0
              ? 'btlApplicationForm.APPLICANTS_INDIVIDUAL.noIndividual.label'
              : 'btlApplicationForm.APPLICANTS_INDIVIDUAL.addAnotherIndividual.label',
        })}
      />
    </Card>
  );

  return (
    <Flex gap={16} vertical>
      <h3 className={'mb-0'}>{formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.individual.label' })}</h3>
      <Flex gap={16} wrap align={'start'}>
        {individualCards}
        {addIndividualCard}
      </Flex>
      {(isFormEditable || data?.borrower.companies.length || 0 > 0) && (
        <>
          <h3 className={'my-0'}>{formatMessage({ id: 'btlApplicationForm.APPLICANTS_COMPANY.company.label' })}</h3>
          <Flex gap={16} wrap>
            <CompanyCard
              companies={data?.borrower.companies}
              applicationId={id}
              refetch={refetch}
              isEditable={!!isFormEditable}
            />
          </Flex>
        </>
      )}
    </Flex>
  );
};

export default Applicants;
